import * as Sentry from '@sentry/react';
import { Apollo } from 'src/gql/generated';

/** The run behind a playbook, as the detail page needs to read it. */
export interface PlaybookGenerationProgress {
  generatedCategoryCount: number;
  id: string;
  failedAt?: string | null;
  failedReason?: string | null;
  finishedAt?: string | null;
  openCategory?: { id: string; name: string } | null;
  startedAt?: string | null;
}

/**
 * What a generation event means for the page showing that run's playbook.
 *
 * - `progress`: the run wrote something; merge it into the cached playbook.
 * - `failed`: the run is over; the job in the cache already says why, and
 *   the playbook may be gone, so asking again would only raise an error.
 * - `gone`: the job was deleted, which is how a stopped run reads. The
 *   playbook keeps what was written and no longer points at a job, so it is
 *   asked for again to drop the banner; one that wrote nothing was removed
 *   with the job and comes back as not found rather than as an error.
 * - `ignore`: some other run's event; the subscription is org-wide.
 */
export type GenerationEventOutcome = 'failed' | 'gone' | 'ignore' | 'progress';

/** The shape of a generation event this page cares about. */
export interface GenerationEvent {
  generationJobId: string;
  generationJob?: {
    failedAt?: string | null;
  } | null;
}

/**
 * Whether a run is still writing into this playbook. A run that failed or was
 * cancelled leaves what it managed to build, which is an ordinary playbook and
 * should not read as one still being written.
 */
export function getIsGenerationInFlight(
  generationJob: PlaybookGenerationProgress | null | undefined
): generationJob is PlaybookGenerationProgress {
  if (!generationJob?.startedAt) return false;
  return !generationJob.finishedAt && !generationJob.failedAt;
}

export function getGenerationEventOutcome(
  event: GenerationEvent | null | undefined,
  generationJobId: string | null
): GenerationEventOutcome {
  if (!event || !generationJobId || event.generationJobId !== generationJobId) return 'ignore';
  if (!event.generationJob) return 'gone';
  if (event.generationJob.failedAt) return 'failed';
  return 'progress';
}

type PlaybookDetail = NonNullable<Apollo.GetPlaybookDetailQuery['playbook']>;
type PlaybookDetailCategories = PlaybookDetail['categories'];
type GenerationUpdatedEvent =
  Apollo.OnPlaybookGenerationJobUpdatedSubscription['playbookGenerationJobUpdated'];

/**
 * Merges a generation event into the detail query: new categories and rules
 * append as they arrive, and an empty card that was never filled is dropped
 * once it is no longer the open category.
 */
export function applyPlaybookGenerationUpdatedEvent(
  previous: Apollo.GetPlaybookDetailQuery,
  event: GenerationUpdatedEvent
): Apollo.GetPlaybookDetailQuery {
  const playbook = previous.playbook;
  if (!playbook) return previous;
  if (event.generationJob?.playbook && event.generationJob.playbook.id !== playbook.id)
    return previous;

  const named = event.generationJob?.playbook;
  const generationJob = event.generationJob ?? playbook.generationJob;
  const withCategory = appendCreatedCategory(playbook.categories, event.createdCategory);
  const withRule = appendCreatedRule(withCategory, event.createdRule);

  return {
    ...previous,
    playbook: {
      ...playbook,
      ...(named ? { description: named.description, title: named.title } : {}),
      categories: dropUnfilledCategories(withRule, generationJob),
      generationJob,
    },
  };
}

/** The card for a category the model just opened, empty until its rules arrive. */
function appendCreatedCategory(
  categories: PlaybookDetailCategories,
  created: GenerationUpdatedEvent['createdCategory']
): PlaybookDetailCategories {
  if (!created) return categories;
  if (categories.some(category => category.id === created.id)) return categories;

  return [...categories, { ...created, rules: created.rules ?? [] }];
}

/** Files a finished rule under its own category, leaving the others untouched. */
function appendCreatedRule(
  categories: PlaybookDetailCategories,
  created: GenerationUpdatedEvent['createdRule']
): PlaybookDetailCategories {
  if (!created) return categories;

  return categories.map(category => {
    if (category.id !== created.category.id) return category;
    if (category.rules.some(rule => rule.id === created.id)) return category;

    return { ...category, rules: [...category.rules, created] };
  });
}

/**
 * Hides a category the model opened and never filled, once it has moved on to
 * another one. A run that is over keeps everything it wrote, empty or not.
 */
function dropUnfilledCategories(
  categories: PlaybookDetailCategories,
  generationJob: PlaybookGenerationProgress | null | undefined
): PlaybookDetailCategories {
  if (!getIsGenerationInFlight(generationJob)) return categories;

  const openCategoryId = generationJob.openCategory?.id ?? null;
  return categories.filter(category => category.rules.length > 0 || category.id === openCategoryId);
}

/**
 * Follows the run writing into the open playbook.
 *
 * Category and rule payloads are merged into the cached detail query so the
 * page does not refetch the whole playbook on every rule. A cancelled job is
 * deleted, which the event cannot carry as a nested playbook, so that case
 * still asks for the playbook again.
 *
 * A failure is the one event that must not trigger a refetch. A run that
 * failed with nothing written takes its empty playbook with it, and asking
 * the server for a playbook that no longer exists is how the page came to show
 * an authorization error a few seconds after being opened. The event's job
 * lands in the cache on its own, which is what flips the page into its failed
 * state.
 */
export function usePlaybookGenerationProgress({
  generationJobId,
  isInFlight,
  onGone,
  playbookId,
}: {
  generationJobId: string | null;
  isInFlight: boolean;
  onGone: () => Promise<unknown>;
  playbookId: string | null;
}): void {
  Apollo.useOnPlaybookGenerationJobUpdatedSubscription({
    ignoreResults: true,
    onData: ({ client, data }) => {
      const event = data.data?.playbookGenerationJobUpdated;
      const outcome = getGenerationEventOutcome(event, generationJobId);
      switch (outcome) {
        case 'failed':
        case 'ignore':
          break;
        case 'gone':
          void onGone();
          break;
        case 'progress':
          if (!playbookId || !event) break;

          client.cache.updateQuery<Apollo.GetPlaybookDetailQuery>(
            {
              query: Apollo.GetPlaybookDetailDocument,
              variables: { id: playbookId },
            },
            previous => {
              if (!previous) return previous;
              return applyPlaybookGenerationUpdatedEvent(previous, event);
            }
          );
          break;
      }
    },
    onError: error => {
      Sentry.captureException(error, { extra: { subscription: 'PlaybookGenerationJobUpdated' } });
    },
    skip: !isInFlight || !generationJobId,
  });
}
